import { useState } from "react";
import { PageHeader, useResource, friendlyTime } from "@/admin/shared";
import { Button, Field, Input, Select, Modal, StatusPill } from "@/admin/ui";
import CredentialModal from "@/admin/CredentialModal";
import DataTable from "@/admin/DataTable";
import { api, formatApiError } from "@/admin/api";

const PLAN_TYPES = ["trial", "starter", "growth", "enterprise"];
const STATUSES = ["active", "trial", "suspended", "inactive"];

const empty = {
    name: "",
    industry: "",
    country: "",
    currency: "INR",
    team_size: 1,
    plan_type: "trial",
    status: "active",
    ceo_name: "",
    ceo_email: "",
    logo: "",
    admin_name: "",
    admin_email: "",
};

export default function Companies() {
    const { data, loading, refresh, error } = useResource("/companies");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [cred, setCred] = useState(null);
    const [pwMode, setPwMode] = useState("auto");
    const [pwValue, setPwValue] = useState("");
    const [ceoPwMode, setCeoPwMode] = useState("auto");
    const [ceoPwValue, setCeoPwValue] = useState("");

    function openCreate() {
        setEditing(null);
        setForm(empty);
        setPwMode("auto");
        setPwValue("");
        setCeoPwMode("auto");
        setCeoPwValue("");
        setErr("");
        setOpen(true);
    }
    function openEdit(c) {
        setEditing(c);
        setForm({ ...empty, ...c });
        setCeoPwMode("auto");
        setCeoPwValue("");
        setErr("");
        setOpen(true);
    }

    async function onSave(e) {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const payload = { ...form, team_size: Number(form.team_size) || 1 };
            if (editing) {
                await api.patch(`/companies/${editing.id}`, payload);
                setOpen(false);
                await refresh();
            } else {
                const r = await api.post("/companies", {
                    ...payload,
                    password_mode: pwMode,
                    password: pwMode === "manual" ? pwValue : "",
                });
                const entries = [];
                const ac = r.data?.admin_credentials;
                if (ac?.temp_password) {
                    entries.push({ roleLabel: "Company Admin", email: ac.email, password: ac.temp_password });
                } else if (ac?.note) {
                    alert(ac.note);
                }
                if (form.ceo_email && r.data?.id) {
                    try {
                        const cr = await api.post(`/companies/${r.data.id}/ceo-credentials`, {
                            password_mode: ceoPwMode,
                            password: ceoPwMode === "manual" ? ceoPwValue : "",
                            ceo_email: form.ceo_email,
                            ceo_name: form.ceo_name,
                        });
                        if (cr.data?.temp_password) {
                            entries.push({ roleLabel: "CEO", email: cr.data.email, password: cr.data.temp_password });
                        }
                    } catch (e3) {
                        alert("Company created, but CEO login was not created: " + formatApiError(e3));
                    }
                }
                setOpen(false);
                await refresh();
                if (entries.length) setCred({ entries });
            }
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    }

    async function resetCeoQuick(c) {
        if (!c.ceo_email) {
            alert("Set a CEO email for this company first (Edit → CEO Email), then reset.");
            return;
        }
        if (!confirm(`Generate a new CEO password for ${c.ceo_email}? Any previous CEO password will stop working.`)) return;
        try {
            const cr = await api.post(`/companies/${c.id}/ceo-credentials`, {
                password_mode: "auto",
                ceo_email: c.ceo_email,
                ceo_name: c.ceo_name,
            });
            if (cr.data?.temp_password) {
                setCred({
                    entries: [{
                        roleLabel: cr.data.created ? "CEO — new login" : "CEO — password reset",
                        email: cr.data.email,
                        password: cr.data.temp_password,
                    }],
                });
            }
        } catch (e) {
            alert(formatApiError(e));
        }
    }

    async function generateCeoCreds() {
        if (!editing) return;
        if (!form.ceo_email) {
            setErr("Add a CEO email above before generating CEO credentials.");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const cr = await api.post(`/companies/${editing.id}/ceo-credentials`, {
                password_mode: ceoPwMode,
                password: ceoPwMode === "manual" ? ceoPwValue : "",
                ceo_email: form.ceo_email,
                ceo_name: form.ceo_name,
            });
            setOpen(false);
            await refresh();
            if (cr.data?.temp_password) {
                setCred({
                    entries: [{
                        roleLabel: cr.data.created ? "CEO — new login" : "CEO — password reset",
                        email: cr.data.email,
                        password: cr.data.temp_password,
                    }],
                });
            }
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function onDelete(c) {
        if (!confirm(`Delete company "${c.name}"?`)) return;
        try {
            await api.delete(`/companies/${c.id}`);
            await refresh();
        } catch (e) {
            alert(formatApiError(e));
        }
    }

    async function toggleStatus(c, next) {
        try {
            await api.patch(`/companies/${c.id}`, { status: next });
            await refresh();
        } catch (e) {
            alert(formatApiError(e));
        }
    }

    const cols = [
        {
            key: "name",
            label: "Company",
            render: (r) => (
                <div>
                    <div className="font-semibold text-black">{r.name}</div>
                    <div className="text-[12px] text-neutral-500">{r.industry || "—"} · {r.country || "—"}</div>
                </div>
            ),
        },
        {
            key: "ceo",
            label: "CEO",
            render: (r) => (
                <div>
                    <div className="text-black">{r.ceo_name || "—"}</div>
                    <div className="text-[12px] text-neutral-500">{r.ceo_email || "—"}</div>
                </div>
            ),
        },
        { key: "team_size", label: "Team", render: (r) => r.team_size },
        {
            key: "plan_type",
            label: "Plan",
            render: (r) => (
                <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-violet-700 bg-violet-50 rounded-full px-2.5 py-1">
                    {r.plan_type}
                </span>
            ),
        },
        { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
        {
            key: "created_at",
            label: "Created",
            render: (r) => <span className="text-[12px] text-neutral-500">{friendlyTime(r.created_at)}</span>,
        },
        {
            key: "actions",
            label: "",
            render: (r) => (
                <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`company-edit-${r.id}`}>
                        Edit
                    </Button>
                    <Button size="sm" variant="subtle" onClick={() => resetCeoQuick(r)} data-testid={`company-ceo-reset-${r.id}`}>
                        Reset CEO PW
                    </Button>
                    {r.status !== "suspended" ? (
                        <Button
                            size="sm"
                            variant="subtle"
                            onClick={() => toggleStatus(r, "suspended")}
                            data-testid={`company-suspend-${r.id}`}
                        >
                            Suspend
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            variant="subtle"
                            onClick={() => toggleStatus(r, "active")}
                            data-testid={`company-activate-${r.id}`}
                        >
                            Activate
                        </Button>
                    )}
                    <Button size="sm" variant="danger" onClick={() => onDelete(r)} data-testid={`company-delete-${r.id}`}>
                        Delete
                    </Button>
                </div>
            ),
            tdClass: "text-right",
        },
    ];

    return (
        <div data-testid="admin-companies-page">
            <PageHeader
                title="Companies"
                subtitle="Create, edit, and suspend the company workspaces on the CEO's GPT platform."
                action={
                    <Button size="lg" onClick={openCreate} data-testid="company-create-btn">
                        + New Company
                    </Button>
                }
            />
            {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50/60 text-rose-700 text-sm px-4 py-3 mb-6">
                    {error}
                </div>
            )}
            <DataTable
                cols={cols}
                rows={data || []}
                rowKey={(r) => r.id}
                loading={loading}
                empty={{ title: "No companies yet", hint: "Create your first company workspace." }}
            />

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={editing ? "Edit Company" : "Create Company"}
                size="lg"
                testid="company-modal"
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={onSave} disabled={busy} data-testid="company-save-btn">
                            {busy ? "Saving…" : editing ? "Save changes" : "Create company"}
                        </Button>
                    </>
                }
            >
                <form onSubmit={onSave} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Company Name">
                        <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-field-name" />
                    </Field>
                    <Field label="Industry">
                        <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
                    </Field>
                    <Field label="Country">
                        <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                    </Field>
                    <Field label="Currency">
                        <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} placeholder="INR" />
                    </Field>
                    <Field label="Team Size">
                        <Input type="number" min="1" value={form.team_size} onChange={(e) => setForm({ ...form, team_size: e.target.value })} />
                    </Field>
                    <Field label="Plan">
                        <Select value={form.plan_type} onChange={(e) => setForm({ ...form, plan_type: e.target.value })}>
                            {PLAN_TYPES.map((p) => <option key={p}>{p}</option>)}
                        </Select>
                    </Field>
                    <Field label="Status">
                        <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                            {STATUSES.map((p) => <option key={p}>{p}</option>)}
                        </Select>
                    </Field>
                    <Field label="Logo URL">
                        <Input value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} placeholder="https://…" />
                    </Field>
                    <Field label="CEO Name">
                        <Input value={form.ceo_name} onChange={(e) => setForm({ ...form, ceo_name: e.target.value })} />
                    </Field>
                    <Field label="CEO Email">
                        <Input type="email" value={form.ceo_email} onChange={(e) => setForm({ ...form, ceo_email: e.target.value })} data-testid="company-ceo-email" />
                    </Field>
                    <div className="sm:col-span-2 rounded-2xl border border-black/10 bg-neutral-50/60 p-4 mt-1" data-testid="ceo-login-section">
                        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-neutral-500">CEO Login</div>
                            {editing && (
                                <Button type="button" size="sm" variant="subtle" onClick={generateCeoCreds} disabled={busy} data-testid="ceo-cred-btn">
                                    {busy ? "Working…" : "Generate / Reset CEO password"}
                                </Button>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4">
                            <label className="inline-flex items-center gap-2 text-[13px] text-black cursor-pointer">
                                <input type="radio" name="ceo-pw-mode" checked={ceoPwMode === "auto"} onChange={() => setCeoPwMode("auto")} data-testid="ceo-pw-mode-auto" />
                                Auto-generate a strong password
                            </label>
                            <label className="inline-flex items-center gap-2 text-[13px] text-black cursor-pointer">
                                <input type="radio" name="ceo-pw-mode" checked={ceoPwMode === "manual"} onChange={() => setCeoPwMode("manual")} data-testid="ceo-pw-mode-manual" />
                                Set password manually
                            </label>
                        </div>
                        {ceoPwMode === "manual" && (
                            <div className="mt-3">
                                <Input type="text" placeholder="Min 8 chars, incl. a letter and a number" value={ceoPwValue} onChange={(e) => setCeoPwValue(e.target.value)} data-testid="ceo-pw-value" />
                            </div>
                        )}
                        <div className="mt-2 text-[12px] text-neutral-500">
                            {editing
                                ? "Creates the CEO's login (or resets it) for the CEO email above. Password is shown once."
                                : "If a CEO email is set, a CEO login is created on save and its password is shown once."}
                        </div>
                    </div>
                    {!editing && (
                        <div className="sm:col-span-2 rounded-2xl border border-black/10 bg-neutral-50/60 p-4 mt-1">
                            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-neutral-500 mb-3">
                                Company Admin Login
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="Admin Name">
                                    <Input
                                        value={form.admin_name}
                                        onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                                        placeholder="e.g. Priya Admin"
                                        data-testid="company-admin-name"
                                    />
                                </Field>
                                <Field label="Admin Email" hint="Left blank → auto-derived as admin@<company>.demo">
                                    <Input
                                        type="email"
                                        value={form.admin_email}
                                        onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                                        placeholder="admin@company.com"
                                        data-testid="company-admin-email"
                                    />
                                </Field>
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-4">
                                <label className="inline-flex items-center gap-2 text-[13px] text-black cursor-pointer">
                                    <input type="radio" name="company-pw-mode" checked={pwMode === "auto"} onChange={() => setPwMode("auto")} data-testid="company-pw-mode-auto" />
                                    Auto-generate a strong password
                                </label>
                                <label className="inline-flex items-center gap-2 text-[13px] text-black cursor-pointer">
                                    <input type="radio" name="company-pw-mode" checked={pwMode === "manual"} onChange={() => setPwMode("manual")} data-testid="company-pw-mode-manual" />
                                    Set password manually
                                </label>
                            </div>
                            {pwMode === "manual" && (
                                <div className="mt-3">
                                    <Input type="text" placeholder="Min 8 chars, incl. a letter and a number" value={pwValue} onChange={(e) => setPwValue(e.target.value)} data-testid="company-pw-value" />
                                </div>
                            )}
                            <div className="mt-2 text-[12px] text-neutral-500">
                                The admin password is shown once after creation. They must change it on first login.
                            </div>
                        </div>
                    )}
                    {err && (
                        <div className="sm:col-span-2 rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5">
                            {err}
                        </div>
                    )}
                </form>
            </Modal>

            <CredentialModal
                open={!!cred}
                onClose={() => setCred(null)}
                entries={cred?.entries}
                email={cred?.email}
                password={cred?.password}
                roleLabel={cred?.roleLabel}
            />
        </div>
    );
}
